/**
 * "Reanalyze selected" — shared by the admin Recordings and Sequences tabs
 * (project_management#514).
 *
 * Deliberately a two-step action rather than a one-click button:
 *
 *  1. **Preview before commit.** Opening the dialog runs
 *     `previewReanalysisBatch`, which executes the real planner without
 *     writing, so the admin sees exactly how many targets will run, which are
 *     skipped and why, and an ETA. A patient's full history can take hours —
 *     "reanalyse this patient" should never be a blind click.
 *  2. **A reason is required.** A reanalysis rewrites stored clinical results
 *     on a Class IIa device, so the record has to say why. The backend enforces
 *     it too; this just fails fast.
 *
 * #574: recordings of SEVERAL patients can be reanalysed in one batch (up to
 * the server's limit, 1000 by default). The patients come from the server
 * preview — each target's owner is read from its own record — so off-page ids
 * from "Select all matching" are counted correctly. A selection spanning
 * patients needs an explicit "reanalyse together" confirmation, which is what
 * sends `allowMultiplePatients`. Sequence batches stay single-patient; the
 * server says so in the preview.
 */
import React from 'react';
import { useMutation, useLazyQuery } from '@apollo/client';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import Alert from '@material-ui/lab/Alert';
import AlertTitle from '@material-ui/lab/AlertTitle';

import { LoadingSpinner } from '../../LoadingSpinner';
import { CREATE_REANALYSIS_BATCH, PREVIEW_REANALYSIS_BATCH } from './queries';
import {
  ReanalysisPreview,
  ReanalysisScope,
  canStart,
  createVariables,
  dialogState,
  listedPatients,
  previewVariables,
  selectedRunnable,
  selectionSummary,
  skipSummary,
  skippedTotal,
} from './reanalyzeDialog';

export type { ReanalysisScope } from './reanalyzeDialog';

export interface ReanalyzeTarget {
  id: string;
}

interface Props {
  scope: ReanalysisScope;
  /** The rows currently ticked in the grid (may span pages). */
  selected: ReanalyzeTarget[];
  className?: string;
  onQueued?: () => void;
}

function PatientTable({ preview, scope }: { preview: ReanalysisPreview; scope: string }) {
  const { rows, hidden } = listedPatients(preview.patients);

  if (!rows.length) return null;

  return (
    <>
      <Table aria-label="Patients in this selection" size="small" style={{ marginTop: 8 }}>
        <TableHead>
          <TableRow>
            <TableCell>Patient userId</TableCell>
            <TableCell align="right">{`${scope}s to run`}</TableCell>
            <TableCell align="right">Skipped</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.userId}>
              <TableCell>{row.userId}</TableCell>
              <TableCell align="right">{row.plannedCount}</TableCell>
              <TableCell align="right">{row.skippedCount}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {hidden > 0 && (
        <Typography color="textSecondary" variant="body2">
          …and {hidden} more patient{hidden === 1 ? '' : 's'}
        </Typography>
      )}
    </>
  );
}

export function ReanalyzeSelectedButton({ scope, selected, className, onQueued }: Props) {
  const [open, setOpen] = React.useState(false);
  const [reason, setReason] = React.useState('');
  const [confirmedTogether, setConfirmedTogether] = React.useState(false);
  const [queued, setQueued] = React.useState(false);

  const targetIds = selected.map((t) => t.id);

  const [runPreview, { data: previewData, loading: previewLoading }] = useLazyQuery(
    PREVIEW_REANALYSIS_BATCH,
    { fetchPolicy: 'network-only' },
  );

  const [createBatch, { data: createData, loading: createLoading, error: createError }] =
    useMutation(CREATE_REANALYSIS_BATCH);

  const preview: ReanalysisPreview | undefined = previewData?.previewReanalysisBatch;
  const created = createData?.createReanalysisBatch;
  const state = dialogState(preview, previewLoading);
  const multiPatient = state.kind === 'ready' && state.multiPatient;

  const openDialog = () => {
    setQueued(false);
    setReason('');
    setConfirmedTogether(false);
    setOpen(true);
    runPreview({ variables: previewVariables(scope, targetIds) });
  };

  const handleConfirm = async () => {
    const result = await createBatch({
      variables: createVariables({
        scope,
        targetIds,
        reason,
        allowMultiplePatients: multiPatient && confirmedTogether,
      }),
    });

    if (result.data?.createReanalysisBatch?.success) {
      setQueued(true);
      onQueued?.();
    }
  };

  const startEnabled = canStart({
    state,
    reason,
    confirmedTogether,
    busy: createLoading,
    plannedCount: preview?.plannedCount ?? 0,
  });
  const skipped = preview ? skippedTotal(preview) : 0;

  return (
    <>
      <Button
        className={className}
        color="secondary"
        disabled={!selected.length}
        variant="contained"
        onClick={openDialog}
      >
        Reanalyze selected
      </Button>

      <Dialog maxWidth="sm" open={open} fullWidth onClose={() => setOpen(false)}>
        <DialogTitle>
          Reanalyze {selected.length} selected {scope}(s)
        </DialogTitle>
        <DialogContent>
          {state.kind === 'loading' && <LoadingSpinner />}

          {state.kind === 'refused' && preview && (
            <>
              <Alert severity="error">
                <AlertTitle>Cannot reanalyze</AlertTitle>
                {state.error}
              </Alert>
              {state.hasBreakdown && (
                <>
                  <DialogContentText component="div" style={{ marginTop: 12 }}>
                    This selection:{' '}
                    <strong>
                      {selectionSummary(scope, {
                        plannedCount: selectedRunnable(preview),
                        patientCount: preview.patientCount,
                      })}
                    </strong>
                    {preview.maxItems ? ` (limit ${preview.maxItems})` : ''}.
                  </DialogContentText>
                  <PatientTable preview={preview} scope={scope} />
                </>
              )}
            </>
          )}

          {state.kind === 'ready' && preview && !queued && (
            <>
              <DialogContentText component="div">
                <strong>{selectionSummary(scope, preview)}</strong> will be queued, running{' '}
                <strong>{preview.concurrency}</strong> at a time.
                <br />
                Estimated duration: <strong>{preview.estimatedDuration}</strong>.
                <br />
                {/* The reassurance that matters most to the operator. */}
                Live uploads are unaffected — a spare analysis worker is always kept free.
              </DialogContentText>

              <Alert severity="info" style={{ marginTop: 12 }}>
                Only one reanalysis batch runs at a time: until this one finishes (about{' '}
                {preview.estimatedDuration}), no other batch can be started.
              </Alert>

              {skipped > 0 && (
                <Alert severity="warning" style={{ marginTop: 12 }}>
                  <AlertTitle>{skipped} will be skipped</AlertTitle>
                  {skipSummary(preview)}
                </Alert>
              )}

              {multiPatient && (
                <>
                  <PatientTable preview={preview} scope={scope} />
                  <FormControlLabel
                    control={(
                      <Checkbox
                        checked={confirmedTogether}
                        color="primary"
                        onChange={(event) => setConfirmedTogether(event.target.checked)}
                      />
                    )}
                    label={`${selectionSummary(scope, preview)} — reanalyse together?`}
                    style={{ marginTop: 8 }}
                  />
                </>
              )}

              <TextField
                id="reanalysis-reason"
                label="Reason (recorded for traceability)"
                margin="normal"
                placeholder="e.g. validating analysis v2.3 respiration"
                value={reason}
                autoFocus
                fullWidth
                required
                onChange={(event) => setReason(event.target.value)}
              />
            </>
          )}

          {createError && (
            <Alert severity="error">
              <AlertTitle>Failed</AlertTitle>
              {createError.message}
            </Alert>
          )}

          {created && !created.success && (
            <Alert severity="error">
              <AlertTitle>Failed</AlertTitle>
              {created.error}
            </Alert>
          )}

          {queued && (
            <Alert severity="success">
              <AlertTitle>Queued</AlertTitle>
              Track progress on the Reanalysis tab.
            </Alert>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpen(false)}>{queued ? 'Close' : 'Cancel'}</Button>
          {!queued && (
            <Button
              color="primary"
              disabled={!startEnabled}
              variant="contained"
              onClick={handleConfirm}
            >
              {createLoading ? 'Queueing…' : 'Start reanalysis'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}
